import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ApiConstants } from 'src/app/base/api-constants';
import { AppGlobalService } from 'src/app/services/app.global.service';

export interface FilterOption {
  label: string;
  value: string;
}

@Injectable({ providedIn: 'root' })
export class MergeHistoryService {
  private readonly http = inject(HttpClient);
  private readonly appGlobalService = inject(AppGlobalService);

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'x-rappit-organization': this.appGlobalService.get('orgId') as string
    });
  }

  getMergeHistory(payload: any): Observable<any> {
    return this.http.post<any>(ApiConstants.getMergeHistory.url, payload, { headers: this.getHeaders() });
  }

  getMergeHistoryById(mergeId: string): Observable<any> {
    const url = ApiConstants.getMergeHistoryById.url.replace('{mergeId}', mergeId);
    return this.http.get<any>(url, { headers: this.getHeaders() });
  }

  /**
   * Get filter options using autosuggest API
   * @param branchId The branch ID
   * @param field The field to get suggestions for (e.g., 'sourceBranchName', 'targetBranchName')
   * @param value Optional search value for filtering suggestions
   * @param branchType Optional branch type ('source' or 'target')
   * @returns Observable with filter options
   */
  getFilterOptions(
    branchId: string,
    field: string,
    value: string | null,
    branchType?: 'source' | 'target'
  ): Observable<{ data: string[] }> {
    const payload: any = {
      start: 0,
      pageSize: 100,
      filter: field,
      value: value ?? ''
    };
    if (branchType) {
      payload.branchType = branchType;
    }
    const url = ApiConstants.getMergeHistoryFilterOptions.url.replace('{branchId}', branchId);
    return this.http.post<Record<string, unknown>[]>(url, payload, { headers: this.getHeaders() }).pipe(
      map((response) => {
        const items = Array.isArray(response) ? response : [];
        const values = [...new Set(
          items
            .map((item) => item[field] as string)
            .filter((val): val is string => val !== null && val !== undefined && val !== '')
        )];
        return { data: values };
      })
    );
  }

  /**
   * Map string array to filter options
   */
  mapToFilterOptions(values: string[]): FilterOption[] {
    return values.map((val) => ({ label: val, value: val }));
  }
}
